import { Button, IconButton, useChatRenderer } from '@form-factory/chatbot-ui';
import type { ChatMessageType } from '@form-factory/schema';
import { FeedbackCategory, Rating } from '@form-factory/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  IconThumbDown,
  IconThumbDownFilled,
  IconThumbUp,
  IconThumbUpFilled,
} from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { memo, useCallback, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { aiCoreApi } from '@/services/aiCoreClient';
import { useBoundStore } from '@/store/store';

import { FeedbackFrame } from './FeedbackFrame';
import { FeedbackTextArea } from './FeedbackTextArea';
import { SimpleTooltip } from '../tooltip/SimpleTooltip';

export type FeedbackProps = {
  message?: ChatMessageType | null;
};

type FeedbackState = {
  rating?: Rating;
  category?: FeedbackCategory | null;
  note?: string | null;
};

export const Feedback = memo(function Feedback({ message }: FeedbackProps) {
  const [visible, setVisible] = useState(false);
  const [isContactRequestVisible, setIsContactRequestVisible] = useState(false);
  const initialFeedback = message?.feedback;
  const [feedback, setFeedback] = useState<FeedbackState | undefined>({
    rating: initialFeedback?.rating,
    category: initialFeedback?.category,
    note: initialFeedback?.note,
  });

  const { t } = useLingui();
  const { append } = useChatRenderer();

  const { setResetConversation } = useBoundStore(
    useShallow(state => ({
      setResetConversation: state.setResetConversation,
    })),
  );

  const { mutateAsync } = useMutation({
    mutationFn: async ({
      note,
      category,
      rating,
    }: {
      note?: string | null;
      category?: FeedbackCategory | null;
      rating: Rating;
    }) => {
      if (!message?.id) {
        return;
      }

      return aiCoreApi.feedback.create({
        messageId: message.id,
        note,
        rating,
        category,
      });
    },
  });

  const handleHumanRequest = useCallback(() => {
    if (!append) return;
    append(t`I would like to contact an operator`);
    setIsContactRequestVisible(false);
  }, [append, t]);

  /**
   * Handle feedback submission and close the feedback form.
   */
  const handleFeedback = useCallback(
    async (
      rating: Rating,
      category?: FeedbackCategory | null,
      note?: string | null,
    ) => {
      try {
        await mutateAsync({ note, category, rating });
        setFeedback(f => ({
          ...f,
          note: note === undefined ? f?.note : note,
          category: category === undefined ? f?.category : category,
          rating,
        }));

        if (rating === Rating.Negative) {
          setResetConversation(true);
        }
      } catch (error) {
        console.error(error);
      }
    },
    [mutateAsync, setResetConversation],
  );

  if (!message?.id) {
    return null;
  }

  return (
    <>
      <div className='w-full'>
        <div className='-mt-4 ml-3 flex flex-row justify-start gap-1'>
          <div className='flex'>
            <SimpleTooltip title={t`Positive rating`} asChild>
              <IconButton
                variant='secondary'
                size='xs'
                circle
                className='w-8'
                onClick={() => {
                  handleFeedback(Rating.Positive, null, null);
                  setVisible(v => false);
                }}
                data-testid='positive-feedback-button'
                data-clicked={
                  feedback?.rating === Rating.Positive ? 'true' : 'false'
                }
              >
                {feedback?.rating === Rating.Positive ? (
                  <IconThumbUpFilled />
                ) : (
                  <IconThumbUp />
                )}
              </IconButton>
            </SimpleTooltip>
          </div>
          <div className='flex'>
            <SimpleTooltip title={t`Negative rating`} asChild>
              <IconButton
                variant='secondary'
                size='xs'
                circle
                className='w-8'
                onClick={() => {
                  handleFeedback(Rating.Negative);
                  setVisible(v => true);
                }}
                data-testid='negative-feedback-button'
                data-clicked={
                  feedback?.rating === Rating.Negative ? 'true' : 'false'
                }
              >
                {feedback?.rating === Rating.Negative ? (
                  <IconThumbDownFilled className='size-4' />
                ) : (
                  <IconThumbDown className='size-4' />
                )}
              </IconButton>
            </SimpleTooltip>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
          >
            <div className='mt-2 flex flex-col items-end gap-2'>
              <FeedbackFrame
                onClose={() => setVisible(false)}
                data-testid='feedback-frame'
              >
                <p className='mb-4'>
                  <Trans>Feedback</Trans>
                </p>
                <div className='flex flex-wrap justify-stretch gap-2'>
                  <Button
                    data-cta='chatbot-feedback-irrelevant-button'
                    size='xs'
                    variant={
                      feedback?.category ===
                      FeedbackCategory.IrrelevantInformation
                        ? 'primary'
                        : 'outline'
                    }
                    circle
                    onClick={() => {
                      handleFeedback(
                        Rating.Negative,
                        FeedbackCategory.IrrelevantInformation,
                      );
                    }}
                    data-testid='irrelevant-feedback-button'
                  >
                    <Trans>Irrelevant</Trans>
                  </Button>
                  <Button
                    data-cta='chatbot-feedback-incorrect-information-button'
                    size='xs'
                    variant={
                      feedback?.category ===
                      FeedbackCategory.IncorrectInformation
                        ? 'primary'
                        : 'outline'
                    }
                    circle
                    onClick={() => {
                      handleFeedback(
                        Rating.Negative,
                        FeedbackCategory.IncorrectInformation,
                      );
                    }}
                  >
                    <Trans>Incorrect information</Trans>
                  </Button>
                  <Button
                    data-cta='chatbot-feedback-insufficient-detail-button'
                    size='xs'
                    variant={
                      feedback?.category === FeedbackCategory.InsufficientDetail
                        ? 'primary'
                        : 'outline'
                    }
                    circle
                    onClick={() => {
                      handleFeedback(
                        Rating.Negative,
                        FeedbackCategory.InsufficientDetail,
                      );
                    }}
                  >
                    <Trans>Insufficient detail</Trans>
                  </Button>
                </div>
              </FeedbackFrame>
              <FeedbackFrame>
                <div className='flex flex-wrap justify-stretch gap-2'>
                  <FeedbackTextArea
                    placeholder={t`Expected answer or solution...`}
                    initialMinHeight='82px'
                    initialValue={feedback?.note || ''}
                    onSubmit={message => {
                      handleFeedback(
                        Rating.Negative,
                        feedback?.category || FeedbackCategory.Other,
                        message,
                      );
                      setVisible(false);
                      setIsContactRequestVisible(true);
                    }}
                    data-testid='feedback-text-area'
                  />
                </div>
              </FeedbackFrame>
            </div>
          </motion.div>
        )}
        {isContactRequestVisible && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
          >
            <FeedbackFrame
              className='mt-2'
              data-testid='feedback-operator-frame'
            >
              <p className='mb-4'>
                <Trans>Do you want to request a human support?</Trans>
              </p>
              <div className='flex flex-wrap justify-stretch gap-2'>
                <Button
                  data-cta='chatbot-feedback-yes-button'
                  size='xs'
                  variant='outline'
                  circle
                  onClick={handleHumanRequest}
                  data-testid='yes-feedback-button'
                >
                  <Trans>Yes</Trans>
                </Button>
                <Button
                  data-cta='chatbot-feedback-no-button'
                  size='xs'
                  variant='outline'
                  circle
                  onClick={() => {
                    setIsContactRequestVisible(false);
                  }}
                  data-testid='no-feedback-button'
                >
                  <Trans>No</Trans>
                </Button>
              </div>
            </FeedbackFrame>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
});
